import { useEffect } from 'react';
import { isSessionExpired, shouldRenewSession } from '@proerp/shared';
import { currentEndReason, endSession, getStoredToken, renewSession } from './auth';

/**
 * Hält die Sitzung am Leben, solange jemand arbeitet — und beendet sie nach
 * 12 h Ruhe — spätestens aber beim nächtlichen Schnitt um 3 Uhr
 * (ADR 0106/0107). Beides steht als `exp` im Token; hier wird nur gelesen.
 *
 * Zwei Hälften, die zusammengehören:
 *
 * 1. **Aktivität verlängert.** Echte Eingaben (Zeigen, Tippen) schieben den
 *    Ablauf nach hinten. Bewusst NICHT die laufenden API-Anfragen der App: Die
 *    feuern auch von selbst (Abfragen, Hintergrund-Aktualisierung), ein offener
 *    Tab würde sich damit ewig selbst am Leben halten — und genau das soll der
 *    Auto-Logout ja verhindern.
 * 2. **Ablauf meldet ab.** Ein Tick prüft regelmäßig, ob das Token vorbei ist.
 *    Ohne das säße man vor einer vollständig gefüllten Oberfläche, in der jede
 *    Aktion mit „Ungültige oder abgelaufene Sitzung" scheitert — der Zustand bis
 *    2026-09-24.
 *
 * `visibilitychange` ist der wichtigste Auslöser: Ein Laptop, der über Nacht
 * zugeklappt war, feuert keine Timer. Erst beim Zurückkommen fällt auf, dass die
 * Sitzung längst vorbei ist — und genau dann soll die Anmeldung stehen, nicht
 * eine tote Seite.
 */

/** Wie oft der Ablauf geprüft wird. Eine Minute reicht — es geht um Stunden. */
const CHECK_INTERVAL_MS = 60_000;

/** Aktivität = bewusste Eingabe. Kein `mousemove`: Ein Mauszucken ist kein Arbeiten. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown'] as const;

export function useSessionWatch(): void {
  useEffect(() => {
    // Läuft eine Verlängerung, wird keine zweite gestartet — sonst löst ein
    // Tipp-Stakkato einen Schwung paralleler Anfragen aus.
    let renewing = false;

    function checkExpiry(): void {
      if (isSessionExpired(getStoredToken())) {
        endSession(currentEndReason());
      }
    }

    function handleActivity(): void {
      const token = getStoredToken();
      // Erst der Ablauf: Wer nach 12 h Ruhe wieder klickt, soll abgemeldet
      // werden — und nicht mit demselben Klick die Sitzung verlängern.
      if (isSessionExpired(token)) {
        endSession(currentEndReason());
        return;
      }
      if (renewing || !shouldRenewSession(token)) return;
      renewing = true;
      void renewSession().finally(() => {
        renewing = false;
      });
    }

    function handleVisibility(): void {
      if (document.visibilityState === 'visible') checkExpiry();
    }

    const timer = window.setInterval(checkExpiry, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibility);
    for (const event of ACTIVITY_EVENTS) {
      // `capture`, damit auch Eingaben zählen, die ein Handler unterwegs stoppt;
      // `passive`, weil hier nie etwas verhindert wird.
      window.addEventListener(event, handleActivity, { capture: true, passive: true });
    }
    checkExpiry();

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity, { capture: true });
      }
    };
  }, []);
}
